import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getDeleteChannelDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-channel.form-default-values';
import { DeleteChannelModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-channel.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoDeleteChannelMutation } from '@service/cpms-bo-charge-point.service';

import {
  DeleteChargePointChannelContextProvider,
  useChargePointChannelContext,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointChannelDeleteFormController } from './charge-point-channel-delete-form/charge-point-channel-delete-form.controller';

interface ChargePointChannelDeleteControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointChannelDeleteController = ({
  onCancel,
  onSuccess,
}: ChargePointChannelDeleteControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [deleteChannelMutation] = useCpmsBoDeleteChannelMutation();
  const chargePoint = useChargePointContext();
  const channel = useChargePointChannelContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<DeleteChannelModel, 'chargePointId' | 'channelId'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-channel-delete.error', { error: t('global.unknown') }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<DeleteChannelModel, 'chargePointId' | 'channelId'>
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    deleteChannelMutation({
      chargePointId: fieldValues.chargePointId,
      channelId: fieldValues.channelId,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-channel-delete.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-channel-delete.error', { error: errorMessage }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getDeleteChannelDefaultValues(chargePoint.id, channel.id);

  return (
    <DeleteChargePointChannelContextProvider data={data}>
      <ChargePointChannelDeleteFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </DeleteChargePointChannelContextProvider>
  );
};
